import { DesignServiceComponent } from '../design-service/design-service.component';
import { DetailsComponent } from '../details/details.component';
import { ProductEditorComponent } from '../product-editor/product-editor.component';
import { TemplateSelectorComponent } from '../template-selector/template-selector.component';
import { UploadMaterialComponent } from '../upload-material/upload-material.component';
import { ValidateDesignComponent } from '../validate-design/validate-design.component';
import { WorkflowSelectorComponent } from '../workflow-selector/workflow-selector.component';
import {
	AfterViewInit,
	Component,
	OnDestroy,
	OnInit,
	QueryList,
	Type,
	ViewChildren,
	inject,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { searchProducts } from '@app/api/Search';
import { RootReducer, Store } from '@app/app.reducers';
import { getGenericCategoryForProduct } from '@app/category/reducers/category.reducer';
import {
	getCartItems as getAllCartItems,
	getCartItem,
	getIsPersistedStateLoaded,
	isCartItem,
} from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import {
	getMyProducts,
	getProductListProduct,
	updateMyProducts,
} from '@app/checkout/modules/checkout-shared/reducers/persisted-list.reducer';
import { StorePersistService } from '@app/checkout/modules/checkout-shared/services/store-persist.service';
import { isDesignerProduct } from '@app/checkout/modules/checkout-shared/utils/checkout-utils';
import { Direction, Step } from '@app/core/models/step';
import { ProductFormOrderDialogComponent } from '@app/dialog/components/product-form-order-dialog/product-form-order-dialog.component';
import { ProductFormQuoteDialogComponent } from '@app/dialog/components/product-form-quote-dialog/product-form-quote-dialog.component';
import { Dialog, open } from '@app/dialog/reducers/dialog.reducer';
import { DialogService } from '@app/dialog/services/dialog.service';
import { ProductSource } from '@app/product/product-types';
import {
	ProductFlowPage,
	Workflow,
	getCurrentStep,
	isStepStateValid,
	resetStep,
	setStep,
	setStepData,
	setStepStateValid,
} from '@app/product/reducers/product-flow.reducer';
import {
	ProductType,
	SelectedOptionsEvent,
	hasFetchedPrice,
	isLoading as isLoadingProducts,
	isError as isProductFetchError,
} from '@app/product/reducers/product.reducer';
import { ProductEditorService } from '@app/product/services/product-editor.service';
import { CustomersCanvasUtil } from '@app/product/utils/customers-canvas';
import { isFormProduct, selectedOptionsAreValid } from '@app/product/utils/product-utils';
import { getStockStatus, isTotalQuantityOutOfStock } from '@app/product/utils/stock-product';
import { popInfoToast } from '@app/shared/reducers/toast.reducer';
import { DataLayer } from '@app/shared/services/data-layer.service';
import { I18nService } from '@app/shared/utils/i18n.service';
import {
	capitalizeFirstLetter,
	distinctUntilKeysChanged,
	filterTruthy,
	firstTruthy,
	getDebug,
} from '@app/shared/utils/util';
import { getUserPermissions } from '@app/user/reducers/user.reducer';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons/faAngleDown';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { faAngleUp } from '@fortawesome/pro-regular-svg-icons/faAngleUp';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { faCartShopping } from '@fortawesome/pro-solid-svg-icons/faCartShopping';
import { faFileArrowDown } from '@fortawesome/pro-solid-svg-icons/faFileArrowDown';
import { faPalette } from '@fortawesome/pro-solid-svg-icons/faPalette';
import { faRectangleList } from '@fortawesome/pro-solid-svg-icons/faRectangleList';
import { faRightLong } from '@fortawesome/pro-solid-svg-icons/faRightLong';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { pluckSuccessData } from '@granodigital/grano-remote-data';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom } from 'rxjs';
import {
	delay,
	distinctUntilChanged,
	distinctUntilKeyChanged,
	filter,
	first,
	map,
	skipUntil,
	switchMap,
	withLatestFrom,
} from 'rxjs/operators';

export enum ProductFlowButtonType {
	Previous = 'previous',
	PreviousConfirm = 'previousConfirm',
	Next = 'next',
	OpenDesigner = 'openDesigner',
	SaveToCart = 'saveToCart',
	SaveToMyProducts = 'saveToMyProducts',
	ValidateDesign = 'validateDesign',
	OpenDialog = 'openDialog',
	Download = 'download',
}

export interface ProductFlowButton {
	id: ProductFlowButtonType;
	label: ((this: ProductFlowComponent) => string) | string;
	cssClass: string;
	icon?: IconDefinition;
	iconSide?: 'left' | 'right';
	hasTooltip?: boolean;
	isValidStateRequired?: boolean;
	action(this: ProductFlowComponent): Promise<void>;
}

/**
 * Step details the buttons, breadcrumb labels etc of an individual product order flow step
 */
export interface ProductFlowStep extends Step {
	key: ProductFlowPage;
	buttons:
		| ProductFlowButton[]
		| ((this: ProductFlowComponent) => ProductFlowButton[] | Promise<ProductFlowButton[]>);
	secondaryButtons?:
		| ProductFlowButton[]
		| ((this: ProductFlowComponent) => ProductFlowButton[] | Promise<ProductFlowButton[]>);
}

export const PREV_BUTTON: ProductFlowButton = {
	id: ProductFlowButtonType.Previous,
	label: $localize`:@@ProductFlowStepButtonLabelPrevious:Previous`,
	cssClass: 'btn-secondary',
	iconSide: 'left',
	icon: faArrowLeft,
	isValidStateRequired: false,
	async action() {
		this.setAdjacentStep(Direction.Previous);
	},
};

export const PREV_CONFIRM_BUTTON: ProductFlowButton = {
	id: ProductFlowButtonType.PreviousConfirm,
	label: $localize`:@@ProductFlowStepButtonLabelPrevious:Previous`,
	cssClass: 'btn-secondary',
	iconSide: 'left',
	icon: faArrowLeft,
	isValidStateRequired: false,
	async action() {
		this.store.dispatch(
			open(Dialog.CONFIRM, {
				title: $localize`:@@ProductFlowStepConfirmPreviousTitle:All changes will be lost if you go back now.`,
				acceptLabel: $localize`:@@ProductFlowStepConfirmPreviousAccept:Go back`,
				rejectLabel: $localize`:@@ProductFlowStepConfirmPreviousReject:Cancel`,
				accept: this.setAdjacentStep.bind(this, Direction.Previous),
			}),
		);
	},
};

export const NEXT_BUTTON: ProductFlowButton = {
	id: ProductFlowButtonType.Next,
	label: $localize`:@@ProductFlowStepButtonLabelNext:Next`,
	cssClass: 'btn-primary',
	icon: faArrowRight,
	iconSide: 'right',
	hasTooltip: true,
	isValidStateRequired: true,
	async action() {
		await this.storeCurrentStepData(Direction.Next);
		this.setAdjacentStep(Direction.Next);
	},
};

export const OPEN_DESIGNER: ProductFlowButton = {
	id: ProductFlowButtonType.OpenDesigner,
	label: $localize`:@@ProductFlowStepButtonLabelProductDesigner:Edit in designer`,
	cssClass: 'btn-primary',
	icon: faPalette,
	iconSide: 'right',
	isValidStateRequired: true,
	async action() {
		await this.storeCurrentStepData(Direction.Next);
		this.store.dispatch(setStep(ProductFlowPage.ProductDesigner));
	},
};

export const SAVE_TO_CART_BUTTON: ProductFlowButton = {
	id: ProductFlowButtonType.SaveToCart,
	label() {
		return this.hasCartItem && !this.isEditingWishlistItem
			? $localize`:@@ProductFlowStepButtonLabelUpdateCartItem:Save changes`
			: $localize`:@@ProductFlowStepButtonLabelAddToCart:Add to cart`;
	},
	cssClass: 'btn-primary',
	icon: faCartShopping,
	iconSide: 'right',
	hasTooltip: true,
	isValidStateRequired: true,
	async action() {
		await this.saveChangesToCart();
	},
};

export const OPEN_DIALOG_BUTTON: ProductFlowButton = {
	id: ProductFlowButtonType.OpenDialog,
	label() {
		const formType = this.product?.metadata?.form_type as string;
		const label =
			this.product?.metadata?.form_button_text || formType === 'quote'
				? 'formRequestQuote'
				: 'formOrder';
		return `productFlowStep.buttonLabel.${label}`;
	},
	cssClass: 'btn-primary',
	icon: faRightLong,
	iconSide: 'right',
	hasTooltip: true,
	isValidStateRequired: true,
	async action() {
		this.openProductFormDialog();
	},
};

export const DOWNLOAD_PDF_BUTTON: ProductFlowButton = {
	id: ProductFlowButtonType.Download,
	label: $localize`:@@ProductFlowStepButtonLabelDownloadPDF:Download PDF`,
	icon: faFileArrowDown,
	iconSide: 'right',
	cssClass: 'btn-secondary',
	isValidStateRequired: true,
	async action() {
		if (this.currentPageComponent && 'downloadPDF' in this.currentPageComponent) {
			await this.currentPageComponent.downloadPDF();
		}
	},
};

export const SAVE_TO_MY_PRODUCTS_BUTTON: ProductFlowButton = {
	id: ProductFlowButtonType.SaveToMyProducts,
	label: $localize`:@@ProductFlowStepButtonLabelSaveToMyProducts:Save to My Products`,
	icon: faRectangleList,
	iconSide: 'right',
	cssClass: 'btn-secondary',
	isValidStateRequired: true,
	async action() {
		this.openSaveToListDialog();
	},
};

export const SAVE_TO_MY_PRODUCTS_BUTTON_PRIMARY: ProductFlowButton = {
	id: ProductFlowButtonType.SaveToMyProducts,
	label() {
		return this.hasCartItem && this.isEditingWishlistItem
			? $localize`:@@ProductFlowStepButtonLabelUpdateCartItem:Save changes`
			: $localize`:@@ProductFlowStepButtonLabelSaveToMyProducts:Save to My Products`;
	},
	icon: faRectangleList,
	iconSide: 'right',
	cssClass: 'btn-primary',
	isValidStateRequired: true,
	async action() {
		this.openSaveToListDialog();
	},
};

/**
 * List of all possible steps a product can have.
 * This is filtered based on product metadata when a product is loaded
 */
export const STEP_POOL: ProductFlowStep[] = [
	// STEP_POOL[0]
	{
		key: ProductFlowPage.ProductDetails,
		breadcrumbLabel: $localize`:@@ProductFlowStepBreadcrumbDetails:Product Details`,
		buttons(): ProductFlowButton[] {
			if (!this.product) return;
			switch (this.product.type) {
				case ProductType.PrintOnDemand:
				case ProductType.Stock:
					return (this.product?.metadata?.allow_checkout ?? true) ? [SAVE_TO_CART_BUTTON] : [];
				case ProductType.Form:
					return [OPEN_DIALOG_BUTTON];
				default:
					return [NEXT_BUTTON];
			}
		},
	},
	// STEP_POOL[1]
	{
		key: ProductFlowPage.Workflow,
		breadcrumbLabel: $localize`:@@ProductFlowStepBreadcrumbWorkflow:Workflow`,
		buttons: [PREV_BUTTON, NEXT_BUTTON],
	},
	// STEP_POOL[2]
	{
		key: ProductFlowPage.ProductDesigner,
		breadcrumbLabel: $localize`:@@ProductFlowStepBreadcrumbProductDesigner:Design Product`,
		buttons: [PREV_CONFIRM_BUTTON, NEXT_BUTTON],
		secondaryButtons(): Promise<ProductFlowButton[]> {
			return firstValueFrom(
				combineLatest([this.productEditor.getAllowDownload$(), this.allowMyProducts$]).pipe(
					first(),
					map(([allowDownload]) => {
						const buttons: ProductFlowButton[] = [];
						if (allowDownload) buttons.push(DOWNLOAD_PDF_BUTTON);
						return buttons;
					}),
				),
			);
		},
	},
	// STEP_POOL[3]
	{
		key: ProductFlowPage.ValidateDesign,
		breadcrumbLabel: $localize`:@@ProductFlowStepBreadcrumbValidateDesign:Validate Design`,
		async buttons(): Promise<ProductFlowButton[]> {
			return this.getPrimaryButtons();
		},
		async secondaryButtons(): Promise<ProductFlowButton[]> {
			return this.getSecondaryButtons();
		},
	},
	// STEP_POOL[4]
	{
		key: ProductFlowPage.Upload,
		breadcrumbLabel: $localize`:@@ProductFlowStepBreadcrumbUpload:Upload Printfile`,
		async buttons(): Promise<ProductFlowButton[]> {
			return this.getPrimaryButtons();
		},
		async secondaryButtons(): Promise<ProductFlowButton[]> {
			return this.getSecondaryButtons();
		},
	},
	// STEP_POOL[5]
	{
		key: ProductFlowPage.SelectTemplate,
		breadcrumbLabel: $localize`:@@ProductFlowStepBreadcrumbTemplate:Select Template`,
		buttons: [PREV_BUTTON, OPEN_DESIGNER],
	},
	// STEP_POOL[6]
	{
		key: ProductFlowPage.DesignService,
		breadcrumbLabel: $localize`:@@ProductFlowStepBreadcrumbDesignService:Design Service`,
		buttons(): ProductFlowButton[] {
			return this.getDefaultSaveToCartButtons();
		},
	},
];

export const STEP_ORDER: ProductFlowPage[] = [
	ProductFlowPage.ProductDetails,
	ProductFlowPage.Workflow,
	ProductFlowPage.SelectTemplate,
	ProductFlowPage.ProductDesigner,
	ProductFlowPage.Upload,
	ProductFlowPage.DesignService,
	ProductFlowPage.ValidateDesign,
];

export const sortByStepKeyOrder =
	(stepKeys: ProductFlowPage[]) =>
	(a: ProductFlowStep, b: ProductFlowStep): number =>
		stepKeys.indexOf(a.key) - stepKeys.indexOf(b.key);

export type ProductFlowPageComponent =
	| DetailsComponent
	| ProductEditorComponent
	| WorkflowSelectorComponent
	| UploadMaterialComponent
	| DesignServiceComponent
	| TemplateSelectorComponent
	| ValidateDesignComponent;

@UntilDestroy()
@Component({
	selector: 'g-product-flow',
	templateUrl: './product-flow.component.html',
	styleUrls: ['./product-flow.component.scss'],
	standalone: false,
})
export class ProductFlowComponent implements OnInit, OnDestroy, AfterViewInit {
	dataLayer = inject(DataLayer);
	productEditor = inject(ProductEditorService);
	store = inject<Store<RootReducer.State>>(Store);
	readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly metaService = inject(Meta);
	private readonly ccUtil = inject(CustomersCanvasUtil);
	private readonly dialogService = inject(DialogService);
	private readonly i18n = inject(I18nService);
	private readonly persist = inject(StorePersistService);

	@ViewChildren('productFlowPage') readonly productFlowPages: QueryList<ProductFlowPageComponent>;
	steps: ProductFlowStep[] = [];
	revertableStepKeys: string[] = [];
	currentStep: ProductFlowStep = STEP_POOL[0];
	currentPageComponent: ProductFlowPageComponent;
	product: api.ProductDto;
	category: api.Category;
	isLoading$: Observable<boolean>;
	recommendations$: Observable<api.ProductDto[]>;
	readonly isStepStateValid$ = new BehaviorSubject<boolean>(null);
	readonly currentStepButtons$ = new BehaviorSubject<ProductFlowButton[]>([]);
	readonly currentStepSecondaryButtons$ = new BehaviorSubject<ProductFlowButton[]>([]);
	readonly isRedirectingToCart$ = new BehaviorSubject<boolean>(false);
	hasCartItem = false;
	isEditingWishlistItem = false;
	hasEnoughStock = true;
	isTotalQuantityOutOfStock = false;
	showPrices: boolean;
	showInternalInfo: boolean;
	price: api.ProductPriceDto;
	isPriceValid: boolean;
	productPage = ProductFlowPage;
	buttonType = ProductFlowButtonType;
	buttonLabelContext = this;
	listFormatter = new Intl.ListFormat(this.i18n.locale, { style: 'long', type: 'conjunction' });
	readonly icons = {
		faSpinner,
		faRightLong,
		faAngleDown,
		faAngleUp,
		faAngleRight,
		faCircleInfo,
	};
	editorInstructionsLink: string;
	readonly buttonTooltipText$ = new BehaviorSubject('');
	readonly allowMyProducts$ = this.store
		.select(getUserPermissions)
		.pipe(map((permissions) => permissions?.allow_my_products ?? false));
	private cartItems: api.CartItemWithProductDto[];
	private readonly _validationTooltipText$ = new BehaviorSubject('');

	private readonly debug = getDebug('ProductFlowComponent');
	readonly validationTooltipText$ = this._validationTooltipText$.pipe(distinctUntilChanged(), delay(0));

	ngOnInit(): void {
		this.resetState();
		this.resetProductEditorState();

		this.initProductHandling();
		this.initStepHandling();
		void this.initValidation();
		this.initMiscellaneous();
	}

	ngOnDestroy(): void {
		// We must reset the editor so that if we add the product again we won't autofill
		// the editor with the old data.
		if (this.productEditor.productEditorChanges$) {
			this.productEditor.productEditorChanges$.next(null);
		}
	}

	ngAfterViewInit(): void {
		// Pick the first flow component since there should always be just one at a time.
		// This fixes the issue where *ngIf breaks ViewChild
		this.productFlowPages.changes
			.pipe(filterTruthy, untilDestroyed(this))
			.subscribe((pages: QueryList<ProductFlowPageComponent>) => {
				this.currentPageComponent = pages.first;
				void this.initStepData();
			});
	}

	setAdjacentStep(direction: Direction): void {
		const currentStepIndex = this.steps.findIndex((step) => step.key === this.currentStep.key);
		const nextStepIndex = Math.min(Math.max(0, currentStepIndex + direction), this.steps.length - 1);
		this.store.dispatch(setStep(this.steps[nextStepIndex].key));
	}

	async setStep(step: ProductFlowStep, replaceStepState = false): Promise<void> {
		if (!step) return;
		this.currentStep = step;
		const buttons = await this.getStepButtons(step, 'buttons');
		const extraButtons = await this.getStepButtons(step, 'secondaryButtons');
		this.currentStepButtons$.next(buttons);
		this.currentStepSecondaryButtons$.next(extraButtons);
		this.navigateToStep(step.key, replaceStepState);
	}

	onSelectWorkflow(workflow: Workflow): void {
		const stepKeys = workflow.steps;
		const steps = STEP_POOL.filter((step) => stepKeys.includes(step.key)).sort(
			sortByStepKeyOrder(stepKeys),
		);
		// Remove previously selected workflow steps and add new ones
		const workflowStepIndex = this.steps.findIndex((step) => step.key === ProductFlowPage.Workflow);
		this.steps = [...this.steps.slice(0, workflowStepIndex + 1), ...steps];
		this.revertableStepKeys = this.steps.map((step) => step.key);
		this.productEditor.patchProductFlowData({
			selectedWorkflow: { key: workflow.key, steps: workflow.steps },
		});
	}

	async onBreadcrumbStepChange({
		step,
		direction,
	}: {
		step: Step;
		direction: Direction;
	}): Promise<void> {
		const productFlorStep = step as ProductFlowStep;
		await this.storeCurrentStepData(direction);
		this.store.dispatch(setStep(productFlorStep.key));
	}

	onSelectedOptionsChange(event: SelectedOptionsEvent): void {
		this.hasEnoughStock = getStockStatus(this.product, event.selectedOptions, this.cartItems);
		this.isTotalQuantityOutOfStock = isTotalQuantityOutOfStock(
			this.hasEnoughStock,
			this.product,
			this.cartItems,
		);
		this.productEditor.updateSelectedOptions(event);
		this.updateValidationTooltip();
	}
	/**
	 * Get an array of specific buttons for each product step for type
	 * Button for previous step is always visible
	 */
	async getPrimaryButtons(): Promise<ProductFlowButton[]> {
		const getProductListStatus = await firstValueFrom(this.route.queryParams);
		const isFromMyProducts = !!getProductListStatus?.productList;
		const hasCartItemId = await firstValueFrom(
			this.route.params.pipe(map((params) => params?.cartItemId as string)),
		);
		if (!(this.product?.metadata?.allow_checkout ?? true)) return [PREV_BUTTON];
		const primaryButton =
			hasCartItemId && isFromMyProducts ? SAVE_TO_MY_PRODUCTS_BUTTON_PRIMARY : SAVE_TO_CART_BUTTON;
		return [PREV_BUTTON, primaryButton];
	}
	/**
	 * Get appropriate secondary buttons for each product step for type
	 */
	async getSecondaryButtons(): Promise<ProductFlowButton[]> {
		const hasCartItemId = await firstValueFrom(
			this.route.params.pipe(map((params) => params?.cartItemId as string)),
		);
		const allowMyProducts = await firstValueFrom(this.allowMyProducts$);
		return !hasCartItemId && allowMyProducts ? [SAVE_TO_MY_PRODUCTS_BUTTON] : [];
	}

	async getStepButtons(
		step: ProductFlowStep,
		buttonProp: 'buttons' | 'secondaryButtons',
	): Promise<ProductFlowButton[]> {
		const buttons = step?.[buttonProp];
		return typeof buttons === 'function' ? Promise.resolve(buttons.call(this)) : buttons;
	}

	getDefaultSaveToCartButtons(): ProductFlowButton[] {
		if (!(this.product?.metadata?.allow_checkout ?? true)) return [PREV_BUTTON];
		return [PREV_BUTTON, SAVE_TO_CART_BUTTON];
	}
	/**
	 * Call button action with context set to component
	 * @param button Button whose action you want to call
	 */
	async callButtonAction(button: ProductFlowButton): Promise<void> {
		await button.action.call(this);
	}

	async saveChangesToCart(): Promise<void> {
		await this.prepareProductForSave();
		await this.productEditor.saveChangesToCart(this.isEditingWishlistItem);

		this.isRedirectingToCart$.next(true);
		await this.router.navigate(
			this.persist.getFlag('enableNewLayout') ? ['/checkout', 'v2', 'cart'] : ['/checkout', 'cart'],
		);
		this.isRedirectingToCart$.next(false);
	}

	async saveProductToList({ name }: { name: string }): Promise<void> {
		this.productEditor.workTitle = name; // Set work title to name given in dialog
		await this.prepareProductForSave();
		combineLatest([
			this.productEditor.getSaveableCartItem$(),
			this.store.select(getMyProducts).pipe(pluckSuccessData),
		])
			.pipe(first())
			.subscribe(([cartItem, list]) => {
				const listProducts = [...(list?.products ?? [])];
				const existingItemIndex = listProducts.findIndex((item) => item.id === cartItem.id);
				cartItem.productFlowData = {
					...cartItem.productFlowData!,
					productSource: ProductSource.MyProducts,
					productOptionsSavedAt: Date.now(),
				};
				if (existingItemIndex === -1) {
					// Add to list
					listProducts.push(cartItem);
				} else {
					// Update existing
					listProducts.splice(existingItemIndex, 1, cartItem);
				}
				this.store.dispatch(updateMyProducts(listProducts));
				void this.router.navigate(['/checkout/my-products']);
			});
	}

	async storeCurrentStepData(direction: Direction): Promise<any> {
		if (!this.currentPageComponent || typeof this.currentPageComponent.collectStepData !== 'function')
			return;
		const data = await this.currentPageComponent.collectStepData(direction);
		this.store.dispatch(setStepData(this.productEditor.itemId, this.currentStep.key, data));
		return data;
	}

	openProductFormDialog(): void {
		const formType = this.product?.metadata?.form_type as string;
		if (!formType) return;
		let dialog: Type<ProductFormQuoteDialogComponent | ProductFormOrderDialogComponent>;
		if (formType === 'quote') dialog = ProductFormQuoteDialogComponent;
		if (formType === 'order') dialog = ProductFormOrderDialogComponent;
		if (!dialog) return;
		this.dialogService.open(dialog, { product: this.product });
	}

	openSaveToListDialog(): void {
		this.store.dispatch(
			open(Dialog.ADD_TO_WISHLIST, {
				accept: (form: { name: string }) => this.saveProductToList(form),
				form: { name: this.productEditor.workTitle },
			}),
		);
	}

	redirectToShoppingCart(): () => Promise<boolean> {
		return () => this.router.navigate(['/checkout/cart']);
	}

	private getEditorInstructionsLink(): string {
		const manulaLinks = new Map([
			['en', 'https://www.manula.com/manuals/grano/mygrano/1/uk/topic/tuotteiden-muokkaaminen/'],
			['fi', 'https://www.manula.com/manuals/grano/mygrano/1/fi/topic/tuotteiden-muokkaaminen/'],
			['sv', 'https://www.manula.com/manuals/grano/mygrano/1/sv/topic/tuotteiden-muokkaaminen/'],
		]);
		return manulaLinks.get(this.i18n.locale);
	}

	private async prepareProductForSave() {
		const stepKeys = this.steps.map((step) => step.key);
		await this.storeCurrentStepData(Direction.Next);
		this.productEditor.patchProductFlowData({ steps: stepKeys });
	}

	private setProductInformation(product: api.ProductDto) {
		this.product = product;
		this.buildSteps();
		void this.preloadEditor();
		this.dataLayer.addProductDetailView(product);
	}

	private setCartItemInformation() {
		this.hasCartItem = !!this.productEditor.cartItem;
		if (!this.hasCartItem) return;
		this.productEditor.patchProductFlowData(this.productEditor.cartItem?.productFlowData);
		const isRestored = this.restoreStepsFromCartItem();
		if (isRestored) {
			// Steps restored so go to product editor
			if (isDesignerProduct(this.productEditor.cartItem)) {
				this.store.dispatch(setStep(ProductFlowPage.ProductDesigner));
				return;
			}
			// Otherwise go to last step
			this.store.dispatch(setStep(this.steps.at(-1).key));
			return;
		}
		// Could not restore steps so they are probably missing from cart item, go to first step
		this.store.dispatch(setStep(this.steps[0].key, true));
	}

	/**
	 * Reset state of product flow
	 */
	private resetState() {
		this.product = undefined;
		this.steps = [];
		this.revertableStepKeys = [];
		this.currentStepButtons$.next([]);
		this.store.dispatch(resetStep());
		this.store.dispatch(setStepStateValid(true));
	}

	private resetProductEditorState() {
		this.productEditor.resetProductState();
		this.productEditor.resetCartItemState();
		this.productEditor.resetSelectedOptions();
		this.productEditor.resetSelectedTemplates();
		this.productEditor.resetProductFlowData();
	}

	/**
	 * Build a list of steps based on product type
	 */
	private buildSteps() {
		const keys = new Set<ProductFlowPage>([ProductFlowPage.ProductDetails]);
		switch (this.product.type) {
			case ProductType.Variable:
				keys.add(ProductFlowPage.Upload);
				break;
			case ProductType.MultipleOption:
				keys.add(ProductFlowPage.Workflow);
				break;
			case ProductType.Editable:
				keys.add(ProductFlowPage.ProductDesigner);
				keys.add(ProductFlowPage.ValidateDesign);
				break;
		}
		this.steps = STEP_POOL.filter((step) => keys.has(step.key)).sort(sortByStepKeyOrder(STEP_ORDER));
		this.revertableStepKeys = this.steps.map((step) => step.key);
	}

	/**
	 * Restore steps based on steps saved to cart
	 */
	private restoreStepsFromCartItem(): boolean {
		const cartItem = this.productEditor.cartItem;
		const stepKeys = new Set<ProductFlowPage>(
			(cartItem?.productFlowData?.steps as ProductFlowPage[]) ?? [],
		);
		if (!stepKeys?.size) return false;
		const steps = STEP_POOL.filter((step) => stepKeys.has(step.key)).sort(
			sortByStepKeyOrder(STEP_ORDER),
		);
		if (!steps?.length) return false;
		this.steps = steps;
		this.revertableStepKeys = this.steps.map((step) => step.key);
		return true;
	}

	/**
	 * If product type is editable or multiple option type, preload the editor resources.
	 */
	private async preloadEditor() {
		const validProductTypes = [ProductType.Editable, ProductType.MultipleOption];
		if (!validProductTypes.includes(this.product.type as ProductType)) return;
		const editor = await this.ccUtil.loadEditorSdk();
		editor.preload();
	}

	/** Set url to match step */
	private navigateToStep(stepKey: ProductFlowPage, replaceUrl = false) {
		if (!this.product) return;
		this.route.queryParamMap
			.pipe(
				first(),
				map((query) => !!query.get('productList')),
			)
			.subscribe((isProductList) => {
				let route = ['/product', this.product.id, this.product.slug, stepKey];
				if (this.hasCartItem) {
					route = [...route, ...['edit', this.productEditor.cartItem.id].filter(Boolean)];
				}
				void this.router.navigate(route, {
					replaceUrl,
					queryParams: isProductList ? { productList: '1' } : {},
				});
			});
	}

	private async findValidStep(
		stepKey: ProductFlowPage | undefined,
		validSteps: ProductFlowStep[],
	): Promise<
		{ validStepKey?: undefined; error: string } | { validStepKey: ProductFlowPage; error?: string }
	> {
		const validStepKey = validSteps?.find((step) => step.key === stepKey)?.key;
		if (!validStepKey) return { error: $localize`:@@ToastInvalidStep:Trying to access invalid step!` };
		switch (validStepKey) {
			case ProductFlowPage.ProductDesigner:
			case ProductFlowPage.Upload:
			case ProductFlowPage.SelectTemplate:
			case ProductFlowPage.Workflow:
			case ProductFlowPage.ValidateDesign: {
				const selectedOptions = await firstValueFrom(this.productEditor.selectedOptions$);
				const isRequiredOptionsSelected = selectedOptionsAreValid(this.product, selectedOptions);
				if (!isRequiredOptionsSelected) {
					return {
						error: $localize`:@@ToastInvalidSelectedOptions:All required options have not been selected`,
					};
				}
				return { validStepKey };
			}
			default:
				return { validStepKey };
		}
	}

	/**
	 * Navigate to a certain step, used when navigating to product flow from elsewhere
	 * @param stepKey Step key to navigate to
	 */
	private async setStepFromUrlParam(stepKey: ProductFlowPage) {
		if (!this.product || !this.steps?.length) return;
		if (!stepKey) {
			// Step key not found, might not be in url so go to first step
			this.store.dispatch(setStep(this.steps[0].key, true));
			return;
		}

		const { validStepKey, error } = await this.findValidStep(stepKey, this.steps);
		if (validStepKey === undefined) {
			// If user has not selected required options, redirect user back to product details page
			// and show message explaining why
			this.store.dispatch(popInfoToast({ title: error }));
			this.store.dispatch(setStep(this.steps[0].key, true));
			return;
		}

		this.store.dispatch(setStep(validStepKey));
	}

	private updateValidationTooltip() {
		const parts: string[] = [];
		if (this.isPriceValid === false)
			parts.push($localize`:@@ProductDetailsPriceCalcFailed:could not calculate price`);
		if (!this.hasEnoughStock && !this.isTotalQuantityOutOfStock)
			parts.push($localize`:@@ProductDetailsProductOutOfStock:product is out of stock`);
		if (!this.hasEnoughStock && this.isTotalQuantityOutOfStock)
			parts.push(
				// eslint-disable-next-line max-len
				$localize`:@@ProductDetailsProductTotalOutOfStock:the combined order amount of the products in the shopping cart and products that are being added to shopping cart exceeds stock level. Please lower the order amount`,
			);
		if (!this.productEditor.isSelectionValid)
			parts.push($localize`:@@ProductDetailsInvalidSelection:product options are invalid`);
		if (this.currentStep?.key === ProductFlowPage.ValidateDesign && !this.isStepStateValid$.getValue())
			parts.push($localize`:@@ValidateDesignMustApproveDesign:Product draft must be approved`);
		if (parts.length > 0) parts[0] = capitalizeFirstLetter(parts[0]);

		this._validationTooltipText$.next(this.listFormatter.format(parts));
	}

	private initProductHandling() {
		this.isLoading$ = this.store.select(isLoadingProducts);

		combineLatest([
			this.route.params.pipe(
				distinctUntilKeyChanged('id'),
				map((p) => Number.parseInt(p.id as string)),
				filter((id) => Number.isFinite(id)),
			),
			combineLatest([
				this.route.params.pipe(
					map((params) => params.cartItemId as string),
					distinctUntilChanged(),
				),
				this.route.queryParamMap.pipe(
					map((query) => !!query.get('productList')),
					distinctUntilChanged(),
				),
				this.store.select(getIsPersistedStateLoaded).pipe(filterTruthy),
			]).pipe(
				this.debug.observe('product$'),
				switchMap(([cartItemId, isProductList]) => {
					this.isEditingWishlistItem = isProductList;
					return isProductList
						? this.store.select(getProductListProduct('my-products', cartItemId))
						: this.store.select(getCartItem(cartItemId));
				}),
			),
		])
			.pipe(untilDestroyed(this))
			.subscribe(([productId, cartItem]) => {
				if (isCartItem(cartItem)) {
					// Set cart item data if returning to editor from shopping cart.
					this.productEditor.connectCartItem(cartItem);
				} else if (!this.isEditingWishlistItem) {
					// Update Product Editor Service if product changes.
					// Don't call this when editing an item from My Products since it causes unnecessary
					// error messages (MTS-229).
					this.productEditor.connectProductById(productId);
				}
			});

		this.productEditor.product$
			.pipe(filterTruthy, distinctUntilChanged(), untilDestroyed(this))
			.pipe(
				filter((product) => {
					const productId = product?.id;
					return Number.isFinite(productId) && productId !== this.product?.id;
				}),
				withLatestFrom(this.route.params.pipe(map((p) => p.stepKey as ProductFlowPage))),
				untilDestroyed(this),
			)
			.subscribe(([product, stepKey]) => {
				// Reset state and set new product info
				this.resetState();
				this.productEditor.resetProductFlowData();
				this.setProductInformation(product);
				this.setCartItemInformation();
				if (!this.hasCartItem) {
					void this.setStepFromUrlParam(stepKey);
				}
			});

		this.store
			.select(isProductFetchError)
			.pipe(untilDestroyed(this), filterTruthy)
			.subscribe(() =>
				// Tell search engines not to index this page if it's a 404
				this.metaService.addTag({ name: 'robots', content: 'noindex, follow' }),
			);
	}

	private initStepHandling() {
		this.store
			.select(getCurrentStep)
			.pipe(
				filterTruthy,
				distinctUntilKeysChanged(['key', 'replaceStepState']),
				map(({ key, replaceStepState }) => ({
					step: this.steps?.find((s) => s.key === key),
					replaceStepState,
				})),
				switchMap(({ step, replaceStepState }) => this.setStep(step, replaceStepState)),
				untilDestroyed(this),
			)
			.subscribe();
	}

	private async initValidation() {
		// Get product data
		const product = await this.productEditor.product$.pipe(firstTruthy).toPromise();
		// Form products are valid if required permissions are found
		if (isFormProduct(product)) {
			this.store
				.select(getUserPermissions)
				.pipe(distinctUntilChanged(), untilDestroyed(this))
				.subscribe((userPermissions) => {
					const isValid = userPermissions?.allow_request_quote ?? true;
					this.isStepStateValid$.next(isValid);
					if (!isValid)
						this.buttonTooltipText$.next(
							$localize`:@@ProductDetailsNoPermissionToRequestQuote:No permission to request quote`,
						);
				});
			return;
		}

		// Construct step validity
		combineLatest([this.store.select(isStepStateValid), this.productEditor.isStateValid$()])
			.pipe(
				delay(0),
				map((results) => results.every(Boolean)),
				distinctUntilChanged(),
				untilDestroyed(this),
			)
			.subscribe((isValid) => {
				this.isStepStateValid$.next(isValid);
			});

		// Update validation tooltip text when step validity changes
		this.isStepStateValid$
			.pipe(
				filter((isValid) => isValid !== null),
				distinctUntilChanged(),
				untilDestroyed(this),
			)
			.subscribe(() => {
				this.updateValidationTooltip();
			});

		// Handle price validity
		combineLatest([
			this.productEditor.price$.pipe(filter(hasFetchedPrice), untilDestroyed(this)),
			this.productEditor.selectedOptions$.pipe(filterTruthy),
		])
			.pipe(untilDestroyed(this))
			.subscribe(([priceState, selectedOptions]) => {
				if (priceState.isError) {
					this.isPriceValid = false;
					this.updateValidationTooltip();
					return;
				}

				if (!this.showPrices) {
					this.isPriceValid = true; // Prices are always valid when they are not shown unless an error occurs
					this.updateValidationTooltip();
					return;
				}

				if (this.showPrices && !!selectedOptions) {
					this.price = priceState.price;
					this.isPriceValid = !!this.price && !!this.price.price_with_tax;
					this.updateValidationTooltip();
				}
			});
	}

	private initMiscellaneous() {
		// Ensure at least one subscription to price handling
		this.productEditor.price$.pipe(untilDestroyed(this)).subscribe();

		// Set showing prices
		this.store
			.select(getUserPermissions)
			.pipe(untilDestroyed(this))
			.subscribe((userPermissions) => {
				this.showPrices = userPermissions?.show_prices ?? true;
				this.showInternalInfo = userPermissions?.show_internal_info ?? false;
			});

		// Set category
		this.productEditor.product$
			.pipe(
				filterTruthy,
				distinctUntilChanged(),
				switchMap((product) => this.store.select(getGenericCategoryForProduct(product, this.i18n))),
				untilDestroyed(this),
			)
			.subscribe((category) => (this.category = category));

		// Keep track of cart items for stock level checks
		this.store
			.select(getAllCartItems)
			.pipe(untilDestroyed(this))
			.subscribe((cartItems) => (this.cartItems = cartItems));

		// Set recommendations, shown if product failed to load
		this.recommendations$ = this.route.params.pipe(
			filter((p) => !!p.slug && !this.product),
			skipUntil(this.isLoading$.pipe(filter((isLoading) => !isLoading))),
			switchMap((p) => searchProducts(p.slug as string, { limit: 6 })),
			map((response) => (response?.data?.body?.hits?.hits || []).map((hit) => hit._source)),
		);

		// Setup manula editor instructions link.
		this.editorInstructionsLink = this.getEditorInstructionsLink();
	}

	private async initStepData() {
		if (!this.currentPageComponent || !('collectStepData' in this.currentPageComponent)) return;
		const data = await this.currentPageComponent.collectStepData();
		if (data) {
			// Wait until redirecting is done since setting step data before that may interrupt the redirect
			await firstValueFrom(this.isRedirectingToCart$.pipe(first((isRedirecting) => !isRedirecting)));
			this.store.dispatch(setStepData(this.productEditor.itemId, this.currentStep.key, data));
		}
	}
}
